import { Box, Typography } from "@mui/material";
import type { NikolaCoachingAnswer as Answer } from "@chessla/shared";

const labelSx = {
  color: "text.secondary",
  fontSize: ".56rem",
  fontWeight: 900,
  letterSpacing: ".12em",
  textTransform: "uppercase",
} as const;

export const NikolaCoachingAnswer = ({ answer }: { answer: Answer }) => (
  <Box data-testid="nikola-coaching-answer" sx={{ display: "grid", gap: { xs: 1.5, sm: 1.75 }, color: "text.primary" }}>
    <Box>
      <Typography sx={labelSx}>COACHING INTERPRETATION</Typography>
      <Typography sx={{ mt: 0.55, maxWidth: "62ch", fontSize: { xs: ".94rem", sm: "1rem" }, lineHeight: 1.65 }}>
        {answer.verdict}
      </Typography>
    </Box>

    {answer.contrast && (
      <Box sx={{ pl: 1.25, borderInlineStart: "2px solid", borderColor: "rgba(240,236,226,.3)" }}>
        <Typography sx={labelSx}>THE DIFFERENCE</Typography>
        <Typography sx={{ mt: 0.45, fontSize: { xs: ".82rem", sm: ".88rem" }, lineHeight: 1.65 }}>
          {answer.contrast}
        </Typography>
      </Box>
    )}

    {answer.line && (
      <Box sx={{ p: { xs: 1.15, sm: 1.35 }, border: "1px solid", borderColor: "rgba(240,236,226,.18)" }}>
        <Typography sx={labelSx}>VERIFIED LINE</Typography>
        <Typography sx={{ mt: 0.35, color: "text.secondary", fontSize: ".65rem" }}>{answer.line.label}</Typography>
        <Box
          component="ol"
          aria-label={answer.line.label}
          sx={{ m: 0, mt: 0.85, pl: 2.8, display: "flex", flexWrap: "wrap", gap: "6px 20px", fontSize: ".8rem" }}
        >
          {answer.line.san.map((san, index) => (
            <Box component="li" key={`${san}:${index}`} sx={{ pl: 0.15 }}>
              {san}
            </Box>
          ))}
        </Box>
      </Box>
    )}

    {answer.reasons.length > 0 && (
      <Box>
        <Typography sx={labelSx}>WHY IT MATTERS</Typography>
        <Box component="ul" sx={{ m: 0, mt: 0.7, pl: 2.35, display: "grid", gap: 0.65, listStyle: "'♟  '" }}>
          {answer.reasons.map((reason, index) => (
            <Box component="li" key={index} sx={{ pl: 0.25, fontSize: { xs: ".8rem", sm: ".86rem" }, lineHeight: 1.6 }}>
              {reason.text}
            </Box>
          ))}
        </Box>
      </Box>
    )}

    {answer.principle && (
      <Box sx={{ p: 1.15, bgcolor: "rgba(240,236,226,.055)" }}>
        <Typography sx={labelSx}>NEXT-TIME CUE</Typography>
        <Typography sx={{ mt: 0.45, fontSize: { xs: ".8rem", sm: ".86rem" }, lineHeight: 1.6 }}>
          {answer.principle}
        </Typography>
      </Box>
    )}
  </Box>
);
